import * as React from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  IconButton,
  CircularProgress,
  TextField,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

export type ConfirmationDialogColor =
  | 'primary'
  | 'secondary'
  | 'error'
  | 'info'
  | 'success'
  | 'warning';

export interface ConfirmationDialogMAIQProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  message: React.ReactNode;
  title?: string;
  loading?: boolean;
  confirmLabel?: string;
  cancelLabel?: string;
  loadingLabel?: string;
  confirmColor?: ConfirmationDialogColor;
  cancelColor?: ConfirmationDialogColor;
  /** When set, user must type this text to enable the confirm button (case-insensitive). */
  confirmRequiredText?: string;
}

/**
 * Shared presentational confirmation dialog (LowLevel).
 * Replaces ConfirmationModal, DeleteConfirmationDialog, and DialogComponent confirms.
 */
export function ConfirmationDialogMAIQ({
  open,
  onClose,
  onConfirm,
  message,
  title = 'Confirm Action',
  loading = false,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  loadingLabel = 'Deleting...',
  confirmColor = 'primary',
  cancelColor = 'secondary',
  confirmRequiredText,
}: ConfirmationDialogMAIQProps) {
  const [confirmInput, setConfirmInput] = React.useState('');

  const isConfirmEnabled = confirmRequiredText
    ? confirmInput.trim().toLowerCase() === confirmRequiredText.toLowerCase()
    : true;

  const handleClose = () => {
    if (loading) return;
    setConfirmInput('');
    onClose();
  };

  const handleConfirm = () => {
    if (!isConfirmEnabled || loading) return;
    setConfirmInput('');
    onConfirm();
  };

  React.useEffect(() => {
    if (!open) setConfirmInput('');
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : handleClose}
      aria-labelledby="confirmation-dialog-maiq-title"
      aria-describedby="confirmation-dialog-maiq-description"
      PaperProps={{
        sx: {
          borderRadius: 1,
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
          overflow: 'hidden',
          minWidth: '400px',
        },
      }}
    >
      <DialogTitle
        id="confirmation-dialog-maiq-title"
        sx={{
          p: 2.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: 'background.paper',
          borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
        }}
      >
        <Typography variant="h6" component="span">
          {title}
        </Typography>
        <IconButton
          onClick={handleClose}
          size="small"
          disabled={loading}
          aria-label="Close"
          sx={{ color: 'text.secondary' }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }} id="confirmation-dialog-maiq-description">
        {typeof message === 'string' ? (
          <Typography variant="body1">{message}</Typography>
        ) : (
          message
        )}
        {confirmRequiredText && (
          <TextField
            fullWidth
            size="small"
            label={`Type "${confirmRequiredText}" to confirm`}
            value={confirmInput}
            onChange={e => setConfirmInput(e.target.value)}
            sx={{ mt: 2 }}
            autoComplete="off"
            disabled={loading}
          />
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          bgcolor: 'background.paper',
          borderTop: '1px solid rgba(0, 0, 0, 0.05)',
        }}
      >
        <Button
          onClick={handleClose}
          variant="outlined"
          size="medium"
          color={cancelColor}
          disabled={loading}
          sx={{
            textTransform: 'none',
            borderRadius: 1,
            px: 2.5,
          }}
        >
          {cancelLabel}
        </Button>
        <Button
          onClick={handleConfirm}
          color={confirmColor}
          variant="contained"
          size="medium"
          autoFocus={!confirmRequiredText}
          disabled={loading || !isConfirmEnabled}
          startIcon={
            loading ? <CircularProgress size={16} color="inherit" /> : undefined
          }
          sx={{
            textTransform: 'none',
            borderRadius: 1,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
            px: 2.5,
          }}
        >
          {loading ? loadingLabel : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ConfirmationDialogMAIQ;
